import React, { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import ClipLoader from "react-spinners/ClipLoader";
import {
  Button,
  TextInput,
  Dropdown,
  CustomCheckBox,
  Image,
  Anchor,
} from "hesus-ui-kit";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import { initMap } from "@utils/mapDoubleMarkerDragged";
import errorIcon from "@icons/ic-error.svg";
import mapBlueIcon from "@icons/ic-map-marker-blue.svg";
import mapOrangeIcon from "@icons/ic-map-marker-orange.svg";
import { addSector } from "@shared/shared.slice";
import { loadSingleCompany as addSingleCompany } from "../../../CompanyList/CompanyList.slice";
import AddressSearchWithMap from "@components/AddressSearch/AddressSearchWithMap";
import ParentCompany from "../ParentCompany";
import { companyTypeOptions } from "@utils/filterOptions";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import {
  identifcationValidation,
  addHyphen,
  prefixRegistration,
  resetIdentifcationNumber,
} from "@utils/identificationRule";
import { getCountryName } from "@utils/utils";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
  RequiredMessage,
} from "@utils/extra-style";

import "@utils/extra-style.scss";
import UserInfo from "@utils/UserInfo";
import {
  ButtonWrapper,
  FormBody,
  SectionContainer,
  OuterWrapper,
  FlexRow,
  GridRow,
  OuterWrapperBackground,
  PaddingDiv,
  CheckboxGridRow,
  ContainerLabel,
  MarginDiv,
  ErrorDiv,
  ErrorLog,
  MapContainer,
  MarginBottom,
} from "./Company.styled";
import "../../AddEditCompany.scss";

const Company = (props) => {
  const {
    t,
    selectedCompany,
    setSelectedCompany,
    isEditMode,
    setTypeData,
    typeData,
    setActiveTab,
    leadId,
  } = props;

  const dispatch = useDispatch();
  const history = useNavigate();
  const params = useParams();
  const generalParams = useGetGeneralParameters();
  const queryParams = new URLSearchParams(window.location.search);
  const queryParamType = queryParams.get("parent");
  const [isCompanyCreating, setIsCompanyCreating] = useState(false);
  const [createCompanyAPIError, setCreateCompanyAPIError] = useState("");
  const [position, setPosition] = useState();
  const [billingPosition, setBillingPosition] = useState();
  const [descriptionData, setDescriptionData] = useState("");
  const [resetMapAddress, setResetMapAddress] = useState(false);

  const [registrationExistInfo, setRegistrationExistInfo] = useState([]);
  const { sectorList } = generalParams;

  const userInfo = UserInfo.getUser();
  const userCountryInfo = UserInfo.countryInfo();
  const userCountryId = userCountryInfo.country.id;
  const countryListForFilter = UserInfo.countryListForFilter(t);

  const companyId = parseInt(params.id, 10);
  const [countryListAvailable, setCountryListAvailable] = useState(false);

  const defaultCoordinates = generalParams.coordinates;

  const countries = generalParams.countryList;

  const language = localStorage.getItem("locale");

  useEffect(() => {
    if (!!countries && Object.keys(countries).length > 0) {
      setCountryListAvailable(true);
    }
  }, [countries]);

  const [formData, setFormData] = useState({
    id: {
      required: false,
      value: selectedCompany?.id ?? "",
      error: "",
    },
    name: {
      required: false,
      value: selectedCompany?.name ?? "",
      error: "",
    },
    companyType: {
      required: true,
      value: "",
      error: "",
    },
    company: {
      required: true,
      value:
        {
          id: selectedCompany?.parentCompany?.id,
          name:
            selectedCompany?.parentCompany?.id +
            " - " +
            selectedCompany?.parentCompany?.name,
        } ?? "",
      error: "",
    },
    registrationNumber: {
      required: false,
      value: prefixRegistration(
        selectedCompany?.platformCountryId,
        selectedCompany?.identificationNumber
      ),
      error: "",
    },
    intracomVatNumber: {
      required: false,
      value: selectedCompany?.intracomVatNumber ?? "",
      error: "",
    },
    shippingAddress: {
      required: false,
      value: selectedCompany?.shippingAddress ?? "",
      error: "",
    },
    shippingSupplement: {
      required: false,
      value: selectedCompany?.shippingAddress?.supplement ?? "",
      error: "",
    },
    billingAddress: {
      required: false,
      value: selectedCompany?.billingAddress ?? "",
      error: "",
    },
    billingSupplement: {
      required: false,
      value: selectedCompany?.billingAddress?.supplement ?? "",
      error: "",
    },
    billingShippingDifferent: {
      required: false,
      value: false,
      error: "",
    },
    phone: {
      required: false,
      value: selectedCompany?.phone ?? "",
      error: "",
    },
    website: {
      required: false,
      value: selectedCompany?.website ?? "",
      error: "",
    },
    activity: {
      required: false,
      value: selectedCompany?.activity?.id ?? "",
      error: "",
    },
    description: {
      required: false,
      value: selectedCompany?.comment ?? "",
      error: "",
    },
    country: {
      required: true,
      value: selectedCompany?.platformCountryId ?? userCountryId,
      error: "",
    },
  });

  useEffect(() => {
    const types = [];
    if (selectedCompany?.isClient) {
      types.push(1);
    }
    if (selectedCompany?.isHaulier) {
      types.push(2);
    }
    if (selectedCompany?.isSupplier) {
      types.push(3);
    }
    if (selectedCompany?.isCentre) {
      types.push(4);
    }
    setTypeData(JSON.stringify(types));

    setFormData({
      id: {
        required: false,
        value: selectedCompany?.id ?? "",
        error: "",
      },
      name: {
        required: false,
        value: selectedCompany?.name ?? "",
        error: "",
      },
      companyType: {
        required: true,
        value: "",
        error: "",
      },
      company: {
        required: true,
        value:
          {
            id: selectedCompany?.parentCompany?.id,
            name:
              selectedCompany?.parentCompany?.id +
              " - " +
              selectedCompany?.parentCompany?.name,
          } ?? "",
        error: "",
      },
      registrationNumber: {
        required: false,
        value: prefixRegistration(
          selectedCompany?.country?.id,
          selectedCompany?.identificationNumber
        ),
        error: "",
      },
      intracomVatNumber: {
        required: false,
        value: selectedCompany?.intracomVatNumber ?? "",
        error: "",
      },
      shippingAddress: {
        required: false,
        value: selectedCompany?.shippingAddress ?? "",
        error: "",
      },
      shippingSupplement: {
        required: false,
        value: selectedCompany?.shippingAddress?.supplement ?? "",
        error: "",
      },
      billingAddress: {
        required: false,
        value: selectedCompany?.billingAddress ?? "",
        error: "",
      },
      billingSupplement: {
        required: false,
        value: selectedCompany?.billingAddress?.supplement ?? "",
        error: "",
      },
      billingShippingDifferent: {
        required: false,
        value: false,
        error: "",
      },
      phone: {
        required: false,
        value: selectedCompany?.phone ?? "",
        error: "",
      },
      website: {
        required: false,
        value: selectedCompany?.website ?? "",
        error: "",
      },
      activity: {
        required: false,
        value: selectedCompany?.activity?.id ?? "",
        error: "",
      },
      description: {
        required: false,
        value: selectedCompany?.comment ?? "",
        error: "",
      },
      country: {
        required: true,
        value: selectedCompany?.platformCountryId ?? userCountryId,
        error: "",
      },
    });
  }, [selectedCompany]);

  // get general parameters
  const companyActivityCache = useRef({});
  const fetchCompanyActivity = () => {
    const countryIdValue = formData?.country?.value;
    if (companyActivityCache.current[countryIdValue]) {
      dispatch(addSector(companyActivityCache.current[countryIdValue]));
      return;
    }
    axios
      .get(`/general/companyActivities?countryId=${countryIdValue}`)
      .then((response) => {
        companyActivityCache.current[countryIdValue] = response.data;
        dispatch(addSector(response.data));
      })
      .catch((error) => {});
  };

  useEffect(() => {
    // if (!sectorList || sectorList?.length === 0) {
      fetchCompanyActivity();
    // }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.country]);

  const fetchRegistrationNumber = () => {
    axios
      .get(
        `/company/checkRegistrationNumber?registrationNumber=${
          formData?.registrationNumber?.value
        }&countryId=${formData?.country?.value}&parentId=${
          JSON.stringify(typeData).indexOf(4) !== -1
            ? formData?.company?.value?.id ?? ""
            : ""
        }`
      )
      .then((response) => {
        if (response?.data?.companies.length > 0) {
          setRegistrationExistInfo(response?.data?.companies);
          formData.registrationNumber.error = "REGISTRATION_USED";
          setFormData({ ...formData });
        } else {
          setRegistrationExistInfo([]);
          formData.registrationNumber.error = "";
          setFormData({ ...formData });
        }
      })
      .catch((error) => {});
  };
  // check registration number is valid
  useEffect(() => {
    const debouncedFn = setTimeout(() => {
      const companyCreated = !!selectedCompany?.id;
      if (
        (formData?.registrationNumber?.value !== "CHE-" &&
          formData?.registrationNumber?.value &&
          !isEditMode &&
          !companyCreated) ||
        ((isEditMode || companyCreated) &&
          formData?.registrationNumber?.value !== "CHE-" &&
          formData?.registrationNumber?.value !== "" &&
          formData?.registrationNumber?.value !==
            selectedCompany?.identificationNumber)
      ) {
        fetchRegistrationNumber();
      }
    }, 900);
    return () => {
      if (debouncedFn) {
        clearTimeout(debouncedFn);
      }
    };
  }, [formData?.company, formData?.companyType, formData?.registrationNumber, selectedCompany]);

  const onCountryChange = (e) => {
    setResetMapAddress(true);
    setFormData({
      ...formData,
      country: {
        value: e.value,
        error: "",
        required: true,
      },
      registrationNumber: {
        value: prefixRegistration(e.value, ""),
        error: "",
        required: false,
      },
      shippingAddress: {
        value: "",
        error: "",
        required: false,
      },
      billingAddress: {
        value: "",
        error: "",
        required: false,
      },
      billingShippingDifferent: {
        value: false,
        error: "",
        required: false,
      },
    });
  };

  useEffect(() => {
    if (resetMapAddress) setResetMapAddress(false);
  }, [resetMapAddress]);

  const onCompanyTypeSelect = (e, typeId) => {
    const tempTypeData = JSON.parse(typeData);
    if (tempTypeData.indexOf(typeId) === -1 && e.currentTarget.checked) {
      tempTypeData.push(typeId);
    } else {
      const index = tempTypeData.indexOf(typeId);
      if (index >= 0) {
        tempTypeData.splice(index, 1);
      }
    }
    setTypeData(JSON.stringify(tempTypeData));
    setFormData({
      ...formData,
      companyType: {
        value: tempTypeData.length > 0 ? tempTypeData.join() : "",
        error: "",
        required: true,
      },
    });
  };

  const onBillingShippingDifferentChange = (e) => {
    setFormData({
      ...formData,
      billingShippingDifferent: {
        value: e.target.checked,
        error: "",
        required: false,
      },
    });
  };

  const onNameChange = (e) => {
    setFormData({
      ...formData,
      name: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onShippingSupplementChange = (e) => {
    setFormData({
      ...formData,
      shippingSupplement: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };
  const onBillingSupplementChange = (e) => {
    setFormData({
      ...formData,
      billingSupplement: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onVatNumberChange = (e) => {
    setFormData({
      ...formData,
      intracomVatNumber: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onRegistrationNumberChange = (e) => {
    setFormData({
      ...formData,
      registrationNumber: {
        value:
          formData.country.value === 8
            ? addHyphen(formData.country.value, e.target.value)
            : e.target.value,
        error: "",
        required: true,
      },
    });
    // fetchRegistrationNumber(e.target.value);
  };

  const onPhoneChange = (e) => {
    setFormData({
      ...formData,
      phone: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onWebsiteChange = (e) => {
    setFormData({
      ...formData,
      website: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onAddressSelect = (selectedAddress) => {
    const address = {
      street: selectedAddress.street.value,
      postalCode: selectedAddress.postal.value,
      city: selectedAddress.city.value,
      country: selectedAddress.countryId.value,
      position: {
        latitude: selectedAddress.latitude.value,
        longitude: selectedAddress.longitude.value,
      },
    };
    setFormData({
      ...formData,
      shippingAddress: {
        value: address,
        error: "",
        required: true,
      },
    });
  };

  const onBillingAddressSelect = (selectedAddress) => {
    const address = {
      street: selectedAddress.street.value,
      postalCode: selectedAddress.postal.value,
      city: selectedAddress.city.value,
      country: selectedAddress.countryId.value,
      position: {
        latitude: selectedAddress.latitude.value,
        longitude: selectedAddress.longitude.value,
      },
    };
    setFormData({
      ...formData,
      billingAddress: {
        value: address,
        error: "",
        required: true,
      },
    });
  };
  const onLatLngUpdate = (lat, lng) => {
    const newPosition = { ...formData.address.value.position };
    newPosition.latitude = lat;
    newPosition.longitude = lng;
    setFormData((formData) => {
      return {
        ...formData,
        shippingAddress: {
          ...formData.shippingAddress,
          value: {
            ...formData.shippingAddress.value,
            position: newPosition,
          },
        },
      };
    });

    setPosition([]);
  };

  const onBillingLatLngUpdate = (lat, lng) => {
    const newPosition = { ...formData.address.value.position };
    newPosition.latitude = lat;
    newPosition.longitude = lng;
    setFormData((formData) => {
      return {
        ...formData,
        billingAddress: {
          ...formData.billingAddress,
          value: {
            ...formData.billingAddress.value,
            position: newPosition,
          },
        },
      };
    });

    setBillingPosition([]);
  };

  const onActivityChange = (e) => {
    setFormData({
      ...formData,
      activity: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };
  const onCancel = () => {
    if (!isEditMode && !leadId) history(`/company/list`);
    else if (!isEditMode && leadId) history(`/lead/view/${leadId}`);
    else {
      history(`/company/view/${companyId}/general-info`);
    }
  };

  useEffect(() => {
    if (
      !!defaultCoordinates?.latitude &&
      !!defaultCoordinates?.longitude &&
      !isEditMode
    )
      initMap(
        defaultCoordinates.latitude,
        defaultCoordinates.longitude,
        setPosition,
        "",
        "",
        setBillingPosition
      );
  }, [defaultCoordinates]);

  useEffect(() => {
    if (
      (!!formData.shippingAddress?.value?.position?.latitude &&
        !!formData.shippingAddress?.value?.position?.longitude) ||
      (!!formData.billingAddress?.value?.position?.latitude &&
        !!formData.billingAddress?.value?.position?.longitude)
    ) {
      initMap(
        formData.shippingAddress?.value?.position?.latitude ?? "",
        formData.shippingAddress?.value?.position?.longitude ?? "",
        setPosition,
        formData.billingAddress?.value?.position?.latitude ?? "",
        formData.billingAddress?.value?.position?.longitude ?? "",
        setBillingPosition
      );
    }
  }, [formData.shippingAddress, formData.billingAddress]);

  useEffect(() => {
    if (
      !!formData.shippingAddress?.value?.position?.latitude &&
      !!formData.shippingAddress?.value?.position?.longitude &&
      !formData.billingShippingDifferent.value &&
      !isEditMode
    )
      initMap(
        formData.shippingAddress?.value?.position?.latitude ?? "",
        formData.shippingAddress?.value?.position?.longitude ?? "",
        setPosition,
        "",
        "",
        setBillingPosition
      );
  }, [formData.billingShippingDifferent.value]);

  useEffect(() => {
    setFormData({
      ...formData,
      description: {
        value: descriptionData,
        error: "",
        required: true,
      },
    });
  }, [descriptionData]);

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.name.value) {
      isFormValid = false;
      data.name.error = t("FIELD_REQUIRED");
    }
    if (JSON.parse(typeData).length === 0) {
      isFormValid = false;
      data.companyType.error = t("FIELD_REQUIRED");
    }
    if (typeData.indexOf(4) === 1 && data.company.value.length === 0) {
      isFormValid = false;
      data.company.error = t("FIELD_REQUIRED");
    }
    if (
      (typeData.indexOf(1) !== -1 ||
        typeData.indexOf(2) !== -1 ||
        typeData.indexOf(3) !== -1) &&
      !data.registrationNumber.value
    ) {
      isFormValid = false;
      data.registrationNumber.error = t("FIELD_REQUIRED");
    }
    // Removed as per HS-3539 point 2 - Company form
    // if (data.registrationNumber.value.length > 0) {
    //   if (!identifcationValidation(countryId, data.registrationNumber.value)) {
    //     isFormValid = false;
    //     data.registrationNumber.error = t("FORMAT_ERROR");
    //   }
    // }
    if (!data.activity.value) {
      isFormValid = false;
      data.activity.error = t("FIELD_REQUIRED");
    }
    if (data.shippingAddress.value.length === 0) {
      isFormValid = false;
      data.shippingAddress.error = t("FIELD_REQUIRED");
    }
    if (
      !!data.billingShippingDifferent.value &&
      data.billingAddress.value.length === 0
    ) {
      isFormValid = false;
      data.billingAddress.error = t("FIELD_REQUIRED");
    }
    if (data.phone.value && !/^[0-9+]{8,14}$/i.test(data.phone.value)) {
      isFormValid = false;
      data.phone.error = t("INVALID_FORMAT");
    }

    if (!isFormValid) {
      setFormData({ ...data });
    }
    return isFormValid;
  };

  const getFormData = () => {
    let billingAdressInfo = formData.billingShippingDifferent.value
      ? formData.billingAddress.value
      : formData.shippingAddress.value;
    if (isEditMode || selectedCompany?.id) {
      billingAdressInfo = formData.billingAddress.value;
    }
    const data = {
      id: isEditMode || selectedCompany?.id ? selectedCompany.id : "",
      name: formData.name.value,
      registrationNumber: formData.registrationNumber.value
        ? resetIdentifcationNumber(
            formData.country.value,
            formData.registrationNumber.value
          )
        : "",
      platformCountryId: formData.country.value,
      activity: { id: formData.activity.value },
      intracomVatNumber: formData.intracomVatNumber.value,
      website: formData.website.value,
      comment: formData.description.value,
      shippingAddress: {
        address: formData.shippingAddress.value.street,
        zipcode: formData.shippingAddress.value.postalCode,
        city: formData.shippingAddress.value.city,
        country: formData.shippingAddress.value.country,
        supplement: formData.shippingSupplement.value,
        position: {
          latitude: formData.shippingAddress.value.position.latitude,
          longitude: formData.shippingAddress.value.position.longitude,
        },
      },
      billingAddress: {
        address: billingAdressInfo.street,
        zipcode: billingAdressInfo.postalCode,
        city: billingAdressInfo.city,
        country: billingAdressInfo.country,
        supplement: formData.billingShippingDifferent.value
          ? formData.billingSupplement.value
          : formData.shippingSupplement.value,
        position: {
          latitude: billingAdressInfo.position.latitude,
          longitude: billingAdressInfo.position.longitude,
        },
      },
      createdBy: userInfo.id,
      isClient: typeData.indexOf(1) !== -1,
      isSupplier: typeData.indexOf(3) !== -1,
      isCentre: typeData.indexOf(4) !== -1,
      isHaulier: typeData.indexOf(2) !== -1,
      parentCompany: formData.company.value,
      phone: formData.phone.value,
      boxFolderId: isEditMode || selectedCompany?.id ? selectedCompany.boxFolderId : "",
      leadId,
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsCompanyCreating(true);
      setCreateCompanyAPIError("");
      if (!isEditMode && !selectedCompany?.id) {
        axios
          .post(`/companies`, getFormData())
          .then((response) => {
            setIsCompanyCreating(false);
            setSelectedCompany(response?.data[0]);
            if (response?.data[0].isCentre) setActiveTab("center");
            else if (response?.data[0].isHaulier) setActiveTab("haulier");
            else if (leadId) history(`/lead/view/${leadId}`);
            else history(`/company/view/${response?.data[0].id}/general-info`);
          })
          .catch((error) => {
            dispatch(addSingleCompany(response.data));
            setIsCompanyCreating(false);
            setCreateCompanyAPIError(error.response?.data?.message ?? "");
          });
      } else {
        axios
          .put(`/companies/${selectedCompany.id || companyId}`, getFormData())
          .then((response) => {
            dispatch(addSingleCompany(response.data));
            setIsCompanyCreating(false);
            setSelectedCompany(response?.data[0]);
            if (response?.data[0].isCentre) setActiveTab("center");
            else if (response?.data[0].isHaulier) setActiveTab("haulier");
            else history(`/company/view/${response?.data[0].id}/general-info`);
          })
          .catch((error) => {
            setIsCompanyCreating(false);
            setCreateCompanyAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  const fetchLeadInfo = () => {
    axios
      .get(`/leads/${leadId}`)
      .then((response) => {
        setFormData({
          ...formData,
          name: {
            value: response?.data?.[0]?.tempCompany?.name,
            error: "",
            required: true,
          },
        });
      })
      .catch((error) => {});
  };

  useEffect(() => {
    if (leadId && !isEditMode) {
      fetchLeadInfo();
    }
  }, [leadId]);

  return (
    <>
      {((isEditMode && selectedCompany?.id && formData.id.value) ||
        (!isEditMode && formData.name)) && (
        <OuterWrapperBackground>
          <PaddingDiv>
            <FormBody>
              <OuterWrapper>
                <FlexRow>
                  <SectionContainer>
                    <div>
                      <GridRow
                        column={
                          countryListForFilter?.length > 1
                            ? "13rem 1fr 1fr"
                            : "1fr 1fr"
                        }
                      >
                        {countryListForFilter?.length > 1 && (
                          <Dropdown
                            id="company-country"
                            label={t("COUNTRY")}
                            name="country"
                            value={formData.country?.value}
                            onChange={onCountryChange}
                            defaultOption={t("COUNTRY")}
                            options={countryListForFilter}
                            labelStyles={labelStyles}
                            inputStyles={
                              formData.country?.error !== ""
                                ? inputStylesError
                                : inputStyles
                            }
                            containerStyles={{ marginBottom: "7px" }}
                            disabled={isEditMode}
                          />
                        )}
                        <TextInput
                          type="text"
                          label={t("COMPANY_NAME")}
                          onChange={onNameChange}
                          name="company-name"
                          id="company-name"
                          value={String(formData.name?.value) || ""}
                          isFloat
                          required
                          errorStyles={{ "font-style": "italic" }}
                          labelStyles={
                            formData.name.error !== ""
                              ? labelStylesError
                              : labelStyles
                          }
                          inputStyles={
                            formData.name.error !== ""
                              ? inputStylesError
                              : inputStyles
                          }
                          containerStyles={{ marginTop: "21px" }}
                          error={formData.name.error}
                          placeholder={t("COMPANY_NAME")}
                        />
                        <div>
                          <div
                            className={
                              formData.companyType.error === ""
                                ? "company__title"
                                : "company__title_error"
                            }
                          >
                            <label htmlFor="company">{t("COMPANY_TYPE")}</label>
                          </div>
                          <CheckboxGridRow>
                            {companyTypeOptions.map((type) => (
                              <CustomCheckBox
                                key={`${type.id}${"-"}${type.name}`}
                                id={type.id}
                                labelText={t(type.name)}
                                dataId={type.id}
                                checked={
                                  JSON.parse(typeData).indexOf(type.id) !== -1
                                }
                                onChange={(e) => {
                                  onCompanyTypeSelect(e, type.id);
                                }}
                              />
                            ))}
                          </CheckboxGridRow>
                          {formData.companyType.error && (
                            <RequiredMessage margin="-20px 0 0 10px">
                              {formData.companyType.error}
                            </RequiredMessage>
                          )}
                        </div>
                      </GridRow>
                      <GridRow column="1fr 1fr">
                        <div>
                          <ParentCompany
                            formData={formData}
                            setFormData={setFormData}
                            isEditMode={isEditMode}
                            queryParamType={queryParamType}
                            objectType={
                              JSON.stringify(typeData).indexOf(4) !== -1
                                ? "supplier"
                                : "company"
                            }
                            countryId={formData?.country?.value}
                          />
                        </div>
                        <TextInput
                          type="text"
                          label={t("COMPANY_VAT_NUMBER")}
                          name="company-vat-number"
                          id="company-vat-number"
                          onChange={onVatNumberChange}
                          value={
                            String(formData.intracomVatNumber?.value) || ""
                          }
                          isFloat
                          labelStyles={labelStyles}
                          containerStyles={{ marginTop: "21px" }}
                          inputStyles={{ padding: "7px 5px 7px 10px" }}
                          error={formData.intracomVatNumber.error}
                          placeholder={t("COMPANY_VAT_NUMBER")}
                        />
                      </GridRow>
                      <GridRow column="1fr 1fr">
                        <TextInput
                          type="text"
                          label={t("COMPANY_REGISTRATION_NUMBER")}
                          name="company-registration-number"
                          id="company-registration-number"
                          onChange={onRegistrationNumberChange}
                          onBlur={fetchRegistrationNumber}
                          value={
                            String(formData.registrationNumber?.value) || ""
                          }
                          isFloat
                          required={
                            !(
                              JSON.stringify(typeData).indexOf(4) !== -1 &&
                              JSON.stringify(typeData).length === 1
                            )
                          }
                          errorStyles={{ "font-style": "italic" }}
                          labelStyles={
                            formData.registrationNumber.error !== ""
                              ? labelStylesError
                              : labelStyles
                          }
                          inputStyles={
                            formData.registrationNumber.error !== ""
                              ? inputStylesError
                              : inputStyles
                          }
                          containerStyles={{ marginTop: "21px" }}
                          error={formData.registrationNumber.error}
                          placeholder={t("COMPANY_REGISTRATION_NUMBER")}
                        />
                        {sectorList?.length > 0 && (
                          <Dropdown
                            id="company-activity"
                            label={t("COMPANY_ACTIVITY")}
                            name="company-activity"
                            value={formData.activity.value}
                            onChange={onActivityChange}
                            defaultOption={t("COMPANY_ACTIVITY")}
                            options={Object.values(sectorList)}
                            required
                            labelStyles={
                              formData.activity.error !== ""
                                ? labelStylesError
                                : labelStyles
                            }
                            inputStyles={
                              formData.activity.error !== ""
                                ? inputStylesError
                                : inputStyles
                            }
                            containerStyles={{ marginBottom: "7px" }}
                            error={formData.activity.error}
                          />
                        )}
                      </GridRow>
                      {formData.registrationNumber.error !== "" &&
                        formData.registrationNumber.error ===
                          "REGISTRATION_USED" && (
                          <GridRow column="1fr 1fr">
                            <ErrorDiv>
                              <RequiredMessage maxWidth="auto">
                                <Image
                                  src={errorIcon}
                                  alt="warning"
                                  styles={{
                                    marginRight: "10px",
                                    width: "18px",
                                  }}
                                />{" "}
                                {t("COMPANY_ERROR_REGISTRATION_NUMBER")}{" "}
                                {registrationExistInfo.map((type) => (
                                  <ErrorLog
                                    key={`${type.id}${"--"}${type.name}`}
                                  >
                                    <Anchor
                                      url={`/company/view/${type.id}/general-info`}
                                      target="_blank"
                                      rel="noreferrer"
                                      styles={{
                                        textDecoration: "underline !important",
                                      }}
                                    >
                                      {type.id + " - " + type.name}
                                    </Anchor>
                                  </ErrorLog>
                                ))}
                              </RequiredMessage>
                            </ErrorDiv>
                          </GridRow>
                        )}
                      <GridRow column="1fr 1fr">
                        <div>
                          {countryListAvailable && (
                            <AddressSearchWithMap
                              isFloat
                              position={position}
                              setPosition={setPosition}
                              title="SHIPPING_ADDRESS"
                              icon={mapOrangeIcon}
                              countryId={formData?.country?.value}
                              name="addShippingAddress"
                              language={language}
                              onAddressSelect={onAddressSelect}
                              onLatLngUpdate={onLatLngUpdate}
                              error={formData.shippingAddress.error}
                              resetMapAddress={resetMapAddress}
                              defaultAddress={
                                formData.shippingAddress.value.country
                                  ? formData.shippingAddress.value?.street +
                                    " " +
                                    formData.shippingAddress.value?.postalCode +
                                    " " +
                                    formData.shippingAddress.value?.city +
                                    " " +
                                    (formData.shippingAddress.value?.country
                                      ? getCountryName(
                                          formData.shippingAddress.value
                                            .country,
                                          countries
                                        )
                                      : "")
                                  : ""
                              }
                            />
                          )}
                          <TextInput
                            type="text"
                            label=""
                            name="company-shipping-supplement"
                            id="company-shipping-supplement"
                            value={
                              String(formData.shippingSupplement?.value) || ""
                            }
                            isFloat
                            onChange={onShippingSupplementChange}
                            labelStyles={labelStyles}
                            containerStyles={{ marginTop: "21px" }}
                            inputStyles={{ padding: "7px 5px 7px 10px" }}
                            error={formData.shippingSupplement.error}
                            placeholder={t("ADDRESS_SUPPLEMENT")}
                          />
                          {!isEditMode && (
                            <MarginDiv>
                              <CustomCheckBox
                                id="billing-shipping-address-different"
                                dataId="100"
                                labelText={t("ADDRESSES_ARE_DIFFERENT")}
                                onChange={(e) => {
                                  onBillingShippingDifferentChange(e);
                                }}
                                checked={
                                  formData.billingShippingDifferent.value
                                }
                              />
                            </MarginDiv>
                          )}
                          {isEditMode && <MarginBottom></MarginBottom>}
                          {(isEditMode ||
                            (formData.billingShippingDifferent.value &&
                              !isEditMode)) && (
                            <>
                              {countryListAvailable && (
                                <AddressSearchWithMap
                                  isFloat
                                  position={billingPosition}
                                  setPosition={setBillingPosition}
                                  title="BILLING_ADDRESS"
                                  icon={mapBlueIcon}
                                  countryId={formData?.country?.value}
                                  name="addBillingAddress"
                                  language={language}
                                  onAddressSelect={onBillingAddressSelect}
                                  onLatLngUpdate={onBillingLatLngUpdate}
                                  error={formData.billingAddress.error}
                                  resetMapAddress={resetMapAddress}
                                  defaultAddress={
                                    formData?.billingAddress?.value
                                      ? formData.billingAddress.value?.street +
                                        " " +
                                        formData.billingAddress.value
                                          ?.postalCode +
                                        " " +
                                        formData.billingAddress.value?.city +
                                        " " +
                                        (formData.billingAddress.value?.country
                                          ? getCountryName(
                                              formData.billingAddress.value
                                                .country,
                                              countries
                                            )
                                          : "")
                                      : ""
                                  }
                                />
                              )}
                              <TextInput
                                type="text"
                                label=""
                                name="company-billing-supplement"
                                id="company-billing-number"
                                value={
                                  String(formData.billingSupplement?.value) ||
                                  ""
                                }
                                isFloat
                                onChange={onBillingSupplementChange}
                                labelStyles={labelStyles}
                                containerStyles={{ marginTop: "21px" }}
                                inputStyles={{
                                  padding: "7px 5px 7px 10px",
                                }}
                                error={formData.billingSupplement.error}
                                placeholder={t("ADDRESS_SUPPLEMENT")}
                              />
                            </>
                          )}
                        </div>
                        <div>
                          <MapContainer id="map" />
                        </div>
                      </GridRow>
                      <GridRow column="1fr 1fr">
                        <TextInput
                          type="text"
                          label={t("COMPANY_PHONE")}
                          name="company-registration-number"
                          id="company-registration-number"
                          value={String(formData.phone?.value) || ""}
                          isFloat
                          onChange={onPhoneChange}
                          labelStyles={labelStyles}
                          containerStyles={{ marginTop: "21px" }}
                          inputStyles={{ padding: "7px 5px 7px 10px" }}
                          error={formData.phone.error}
                          placeholder={t("COMPANY_PHONE")}
                        />
                        <TextInput
                          type="text"
                          label={t("COMPANY_WEBSITE")}
                          name="company-registration-number"
                          id="company-registration-number"
                          value={String(formData.website?.value) || ""}
                          isFloat
                          onChange={onWebsiteChange}
                          labelStyles={labelStyles}
                          containerStyles={{ marginTop: "21px" }}
                          inputStyles={{ padding: "7px 5px 7px 10px" }}
                          error={formData.website.error}
                          placeholder={t("COMPANY_WEBSITE")}
                        />
                      </GridRow>
                      <GridRow column="1fr">
                        <div>
                          <ContainerLabel htmlFor="description">
                            {t("ADDITIONAL_INFORMATION")}
                          </ContainerLabel>
                          <CKEditorText
                            descriptionData={descriptionData}
                            setDescriptionData={setDescriptionData}
                          />
                          {formData.description.error && (
                            <RequiredMessage>
                              {formData.description.error}
                            </RequiredMessage>
                          )}
                        </div>
                      </GridRow>
                    </div>
                  </SectionContainer>
                </FlexRow>
              </OuterWrapper>
            </FormBody>
            <ButtonWrapper>
              {createCompanyAPIError && <div>{createCompanyAPIError}</div>}
              <Button
                styles={{
                  color: "#333",
                  background: "#fff",
                  border: "1px solid #ccc",
                  marginRight: "0.32rem",
                  "&:hover": {
                    backgroundColor: "#ccc",
                  },
                }}
                onClick={onCancel}
              >
                <>{t("CANCEL")}</>
              </Button>

              <Button
                styles={{
                  color: "white",
                  background: "#1BACE4",
                  border: "1px solid #1BACE4",
                  marginRight: "0.32rem",
                  "&:hover": {
                    backgroundColor: "#1589b6",
                  },
                }}
                onClick={onSubmit}
              >
                <>
                  {isCompanyCreating && <ClipLoader size={15} color="#fff" />}
                  {!isCompanyCreating && t("SUBMIT")}
                </>
              </Button>
            </ButtonWrapper>
          </PaddingDiv>
        </OuterWrapperBackground>
      )}
    </>
  );
};
Company.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  selectedCompany: PropTypes.object.isRequired,
  setSelectedCompany: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  setTypeData: PropTypes.func.isRequired,
  typeData: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  leadId: PropTypes.number,
};
Company.defaultProps = {
  id: null,
  isEditMode: false,
  leadId: null,
};

export default withTranslation()(Company);
